import React, { useEffect, useRef, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import EmailMessageThreadTable from '../../../components/tables/EmailMessageThreadTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useEmailMessageThreadApi } from '../../../hooks/useEmailMessageThreadApi';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { EmailMessageThreadDTO } from '../../../services/apiClient';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import GeneralSearch from '../../../components/common/GeneralSearch';
import { ComponentConstants } from '../../../constants/ComponentConstants';
import { useSearchState } from '../../../utils/searchStateUtils/useSearchState';
import { useAdminUsers } from '../../../hooks/useUserApi';
import { getUserId } from '../../../utils/tokenUtils';
import { getAdjustedPageAfterMutation } from '../../../utils/paginationUtils';

// Reply type status GUIDs (mirrored from backend ReplyTypeConstants)
const REPLY_TYPE_PENDING  = 'a8f3d2e1-5c4b-4a89-9d7e-1234567890ab';
const REPLY_TYPE_REPLIED  = '140a448f-c7c6-46ab-b745-885260079ecc';
const REPLY_TYPE_RESOLVED = 'fd3ba65e-65ac-4ba4-93bf-4f2d3b70c1cc';

const STATUS_OPTIONS = [
  { value: REPLY_TYPE_PENDING,  label: 'Pending' },
  { value: REPLY_TYPE_REPLIED,  label: 'Replied' },
  { value: REPLY_TYPE_RESOLVED, label: 'Resolved' },
];

const EmailMessageThreadList: React.FC = () => {
  const { search, remove, extract, markAsReplied, markAsResolved, isLoading, error } = useEmailMessageThreadApi();
  const { data: adminUsers = [], isLoading: isLoadingAdminUsers } = useAdminUsers();
  const [items, setItems] = useState<EmailMessageThreadDTO[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const { componentName } = useParams<{ componentName?: string }>();

  // Search state persistence (cached in URL + sessionStorage)
  const {
    currentPage,
    pageSize,
    sortBy,
    sortDescending,
    searchFilters,
    showAdvanced,
    setCurrentPage,
    setPageSize,
    setSearchFilters,
    setShowAdvanced,
    clearSearchState,
    commitSearch,
  } = useSearchState('replies', {
    currentPage: 1,
    pageSize: 10,
    sortBy: 'CreatedUtc',
    sortDescending: true,
    searchFilters: {},
    showAdvanced: false,
  });

  // Track if we've applied defaults (only apply once per pathname)
  const hasAppliedDefaultsRef = useRef(false);
  const previousPathnameRef = useRef(location.pathname);

  // Apply defaults on first load if no search params present
  useEffect(() => {
    // Reset defaults flag when pathname changes (navigating between /admin/replies and /admin/replies/component/members)
    if (previousPathnameRef.current !== location.pathname) {
      previousPathnameRef.current = location.pathname;
      hasAppliedDefaultsRef.current = false;
    }

    const params = new URLSearchParams(location.search);
    const reservedParams = ['page', 'pageSize', 'sortBy', 'sortDesc', 'showAdvanced', 'view'];
    const urlFilters: Record<string, any> = {};
    
    params.forEach((value, key) => {
      if (!reservedParams.includes(key) && value) {
        urlFilters[key] = value;
      }
    });

    const hasUrlFilters = Object.keys(urlFilters).length > 0;

    // If landing on page with no search params and haven't applied defaults yet
    if (!hasUrlFilters && !hasAppliedDefaultsRef.current) {
      hasAppliedDefaultsRef.current = true;
      const defaultFilters = {
        adminUserId: getUserId() || undefined,
        replyTypeId: REPLY_TYPE_PENDING,
      };
      // commitSearch will update URL, which triggers the fetch effect below
      commitSearch(defaultFilters, { page: 1, showAdvanced: true });
      return; // Don't fetch yet, let the URL update trigger the next effect
    }

    // Fetch when URL changes (source of truth for data fetching)
    const urlPage = params.get('page');
    const urlPageSize = params.get('pageSize');
    const page = urlPage ? parseInt(urlPage, 10) : currentPage;
    const size = urlPageSize ? parseInt(urlPageSize, 10) : pageSize;

    // Always apply component filter if present in URL
    const componentId = getComponentIdFromName(componentName);
    const effectiveFilters = componentId ? { ...urlFilters, componentId } : urlFilters;

    fetchItems(page, size, effectiveFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, componentName]);

  // Function to get component ID from URL component name
  const getComponentIdFromName = (name: string | undefined): string | null => {
    if (!name) return null;
    switch (name.toLowerCase()) {
      case 'members':        return ComponentConstants.MEMBERS;
      case 'scores':         return ComponentConstants.SCORES;
      case 'activities':     return ComponentConstants.ACTIVITIES;
      case 'endorsements':   return ComponentConstants.ENDORSEMENTS;
      case 'dedicatedcourses': return ComponentConstants.DEDICATED_COURSES;
      case 'rpl':            return ComponentConstants.RPL;
      default:               return null;
    }
  };

  const getComponentDisplayName = (name: string | undefined): string => {
    if (!name) return 'All';
    switch (name.toLowerCase()) {
      case 'members':        return 'Members';
      case 'scores':         return 'Scores';
      case 'activities':     return 'Activities';
      case 'endorsements':   return 'Endorsements';
      case 'dedicatedcourses': return 'Dedicated Courses';
      case 'rpl':            return 'RPL';
      default:               return 'All';
    }
  };

  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Email Message Threads', href: '#' },
    ...(componentName ? [{ label: getComponentDisplayName(componentName), href: '#' }] : []),
  ];

  const activeMenuItem = componentName
    ? `/admin/replies/component/${componentName}`
    : '/admin/replies';

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      // Always apply component filter if present in URL
      const componentId = getComponentIdFromName(componentName);
      const mergedFilters = componentId ? { ...filters, componentId } : filters;

      const response = await search({
        filters: mergedFilters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
      return response?.data?.totalCount || 0;
    } catch (err) {
      console.error('Error fetching Email Message Threads:', err);
      return 0;
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // commitSearch updates URL with filters, then the useEffect will fetch
    commitSearch(searchFilters, { page: 1, showAdvanced });
  };

  const handleClearSearch = () => {
    // Mark that we've cleared, so defaults won't re-apply
    hasAppliedDefaultsRef.current = true;
    clearSearchState();
  };

  /**
   * Refreshes the list after a mutation (delete, update, etc.),
   * automatically adjusting to a valid page if the current page is now out of range.
   */
  const refreshAfterMutation = async () => {
    // Fetch with current page first to get the new total count
    const newTotalCount = await fetchItems(currentPage, pageSize, searchFilters);
    
    // Calculate what page we should be on based on new total
    const adjustedPage = getAdjustedPageAfterMutation(currentPage, pageSize, newTotalCount);
    
    // If we need to move to a different page, update and refetch
    if (adjustedPage !== currentPage) {
      setCurrentPage(adjustedPage);
      await fetchItems(adjustedPage, pageSize, searchFilters);
    }
  };

  const handleDelete = async (dto: EmailMessageThreadDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;
    try {
      await remove(dto.id || '');
      await refreshAfterMutation();
    } catch (err) {
      console.error('Error deleting EmailMessageThread:', err);
    }
  };

  const handleMarkAsReplied = async (dto: EmailMessageThreadDTO) => {
    try {
      await markAsReplied(dto.id || '');
      await refreshAfterMutation();
    } catch (err) {
      console.error('Error marking EmailMessageThread as replied:', err);
    }
  };

  const handleMarkAsResolved = async (dto: EmailMessageThreadDTO) => {
    try {
      await markAsResolved(dto.id || '');
      await refreshAfterMutation();
    } catch (err) {
      console.error('Error marking EmailMessageThread as resolved:', err);
    }
  };

  const handleExport = async () => {
    try {
      const response = await extract({
        filters: searchFilters,
        page: 1,
        pageSize: 9999999,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      if (response.data) window.location.href = response.data;
    } catch (err) {
      console.error('Error exporting Email Message Threads:', err);
    }
  };

  return (
    <AdminLayout
      activeMenuItem={activeMenuItem}
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Manage.EmailMessageThread.View"
    >
      <Card
        title={componentName ? `${getComponentDisplayName(componentName)} - Message Replies` : 'Manage Email Message Threads'}
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <div className="flex items-center space-x-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? 'Basic Search' : 'Advanced Search'}
            </Button>
            <Button type="button" variant="secondary" onClick={handleExport}>
              Export
            </Button>
          </div>
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          {/* Show either quick search or advanced search, not both */}
          {!showAdvanced ? (
            <GeneralSearch
              searchFilters={searchFilters}
              setSearchFilters={setSearchFilters}
            />
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 mt-4">
                <FormField
                  id="adminUserId"
                  label="Admin"
                  type="dropdown"
                  value={searchFilters.adminUserId || ''}
                  onChange={(e) => setSearchFilters({ ...searchFilters, adminUserId: e.target.value })}
                  placeholder="All admins"
                  options={adminUsers.map((u) => ({
                    value: u.id || '',
                    label: `${u.firstName || ''} ${u.lastName || ''}`.trim(),
                  }))}
                  isLoading={isLoadingAdminUsers}
                />
                <FormField
                  id="replyTypeId"
                  label="Status"
                  type="dropdown"
                  value={searchFilters.replyTypeId || ''}
                  onChange={(e) => setSearchFilters({ ...searchFilters, replyTypeId: e.target.value })}
                  placeholder="All statuses"
                  options={STATUS_OPTIONS}
                />
                <FormField
                  id="idPassportNumber"
                  label="ID / Passport Number"
                  value={searchFilters.idPassportNumber || ''}
                  onChange={(e) => setSearchFilters({ ...searchFilters, idPassportNumber: e.target.value })}
                  placeholder="Search by ID number"
                />
              </div>

              <div className="flex space-x-4 mb-4">
                <Button variant="secondary" onClick={handleClearSearch}>
                  Clear Search
                </Button>
                <Button type="submit">Search</Button>
              </div>
            </>
          )}
        </form>

        <EmailMessageThreadTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/replies/${dto.id}`)}
          handleDelete={handleDelete}
          handleMarkAsReplied={handleMarkAsReplied}
          handleMarkAsResolved={handleMarkAsResolved}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default EmailMessageThreadList;
